/**
 * Public paths shared by the build (Vite plugins) and the browser code.
 * Plain constants only: this file is imported from Node at build time.
 */

/** ONNX Runtime builds, each in a folder per version so a cached file never meets a different runtime. */
export const ortAssetPath = (version: string): string => `vendor/ort/${version}/`;

/** The two ONNX Runtime builds we load: plain CPU, and the WebGPU-capable one. */
export const ORT_VARIANTS = {
  cpu: 'ort-wasm-simd-threaded',
  webgpu: 'ort-wasm-simd-threaded.asyncify',
} as const;

export type OrtVariant = keyof typeof ORT_VARIANTS;

/**
 * The WebGPU build is over the host's 25 MiB file limit (see build/host-limits.ts),
 * so it ships gzipped (≈ 6.5 MB instead of 27 MB) and is unpacked in the browser.
 */
const GZIPPED_VARIANTS: ReadonlySet<OrtVariant> = new Set(['webgpu']);

export interface OrtFiles {
  /** Emscripten loader script. */
  readonly mjs: string;
  /** WebAssembly binary as published (with `.gz` when {@link gzipped}). */
  readonly wasm: string;
  readonly gzipped: boolean;
}

export function ortFiles(variant: OrtVariant): OrtFiles {
  const name = ORT_VARIANTS[variant];
  const gzipped = GZIPPED_VARIANTS.has(variant);
  return { mjs: `${name}.mjs`, wasm: gzipped ? `${name}.wasm.gz` : `${name}.wasm`, gzipped };
}

/**
 * Model files published under `public/models/`. A changed model gets a new
 * name (build/model-files.test.ts pins each one's hash); devices drop cached
 * files that are no longer listed here.
 */
export const PUBLISHED_MODEL_FILES = ['plate-yolov9t-384.onnx', 'probe-plate.webp', 'selfie-segmentation.onnx', 'yunet-2023mar-dynamic.onnx'] as const;

/** Whisper models for captions, from Hugging Face (word timestamps included). */
export const CAPTION_MODELS = {
  fast: 'onnx-community/whisper-base_timestamped',
  accurate: 'onnx-community/whisper-small_timestamped',
} as const;
